import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import { Shipment, WorkflowRule } from '@/model';

/*
 * To add a new rule:
 *
 * 1. Create the rule as an object that implements the `WorkflowRule` interface
 * 2. Add the rule to the `workflowRules` array
 *
 * The composable will compute all rules and determine if the `shipment`
 * argument has any non-default rules,
 * e.g. `UseShipmentWorkflowRulesResponse['shipmentWorkflowRules']`.
 * The non-default rules are displayed in the `ShipmentWorkflowRuleSection`
 */

export interface UseShipmentWorkflowRulesResponse {
  appliedWorkflowRules: ComputedRef<WorkflowRule[]>;
  workflowRules: WorkflowRule[];
}

export const useShipmentWorkflowRules = (
  shipment: MaybeRefOrGetter<Shipment>,
): UseShipmentWorkflowRulesResponse => {
  const { t } = useI18n();

  const requireBothArrivalAndDepartureOnStops: WorkflowRule = {
    key: 'requireBothArrivalAndDepartureOnStops',
    label: t('shipments.requireBothArrivalAndDepartureOnStops'),
    helpText: t('shipments.requireBothDatesInfo'),
    defaultValue: false,
    getValue: (shipment: MaybeRefOrGetter<Shipment>) => {
      const rawShipment = toValue(shipment);
      return rawShipment.requireBothArrivalAndDepartureOnStops;
    },
  };

  const workflowRules = [requireBothArrivalAndDepartureOnStops];

  const appliedWorkflowRules = computed(() => {
    return workflowRules
      .map((r) => {
        r.cachedValue = r.getValue(shipment);
        return r;
      })
      .filter((r) => r.cachedValue !== r.defaultValue);
  });

  return {
    appliedWorkflowRules,
    workflowRules,
  };
};
